import axios from 'axios';

// api/chat/chatApi.ts
import { api } from 'api/v1/api';

export interface ChatUser {
  id_user: number;
  id_profile: number;
  name: string;
  surname: string;
  patronymic?: string;
  profile_photo_token?: string;
  is_deleted: boolean;
  is_online: boolean;
  /** prod1: когда собеседник был в сети (websocket-hub / heartbeat) */
  last_seen_at?: string | null;
  role_text: string;
  id_role: number;
}

export interface LastMessage {
  id: number;
  msg: string;
  user_id: number;
  date_send: string;
}

export interface ChatMessageResponse {
  id: string;
  text: string;
  sender_id: number;
  receiver_id: number;
  created_at: string;
  updated_at: string;
  status: 'sent' | 'delivered' | 'read';
  sender_role?: 'tutor' | 'student' | 'system';
  message_types?: string[];
}

export interface RelatedBulletinResponse {
  bulletin_id: number;
  bulletin_response_id: number;
  status: string;
  active: boolean;
  bulletin_closed?: boolean;
  date_create: string | null;
  title: string;
  subcategory_ru?: string;
  category_ru?: string;
}

export interface ContactShareStateResponse {
  eligible: boolean;
  shared: boolean;
  viewer_role?: string | null;
  user_consented: boolean;
  other_consented: boolean;
  tutor_consented: boolean;
  student_consented: boolean;
  user_has_contacts?: boolean;
  missing_fields?: string[];
  contacts_shared_at?: string | null;
  review_window_ends_at?: string | null;
  review_eligible_now?: boolean;
  can_leave_review?: boolean;
  has_left_review?: boolean;
  deal_auto_closed?: boolean;
  seconds_until_review_window?: number | null;
}

export interface ChatRoomResponse {
  id: number;
  room_id: string;
  active: boolean;
  date_start: string;
  date_end: string | null;
  other_user: ChatUser | null;
  last_message: LastMessage | null;
  message_count: number;
  unread_count?: number;
  related_bulletins?: RelatedBulletinResponse[];
  moderation_blocked?: boolean;
  moderation_blocked_reason?: string | null;
}

export interface GetChatRoomsResponse {
  user_id: number;
  count: number;
  items: ChatRoomResponse[];
}

interface ChatHistoryResponse {
  chat_id: number;
  count: number;
  items: Array<{
    id: number;
    chat_id: number;
    user_id: number;
    msg: string;
    sender_role?: string | null;
    types?: string[];
    /** prod1: статус своего сообщения для зрителя (sent / delivered / read) */
    status?: 'sent' | 'delivered' | 'read' | null;
    date_send: string;
    date_change: string;
  }>;
  user_id: number;
  contact_share?: ContactShareStateResponse;
}

export type ChatViewerRole = 'tutor' | 'student';
export type ChatRoomStatus = 'active' | 'closed';

export interface StudentChatBulletinThreadResponse {
  bulletin_id: number;
  label: string;
  date_create: string | null;
  /** ISO last message / room activity; optional until API ships */
  last_activity?: string | null;
  tutor_count: number;
  chat_count: number;
  unread_count: number;
}

export interface StudentChatBulletinThreadsResponse {
  items: StudentChatBulletinThreadResponse[];
  count: number;
}

export const fetchStudentChatBulletinThreads = async (
  status: ChatRoomStatus = 'active',
): Promise<StudentChatBulletinThreadResponse[]> => {
  const response = await api.get<StudentChatBulletinThreadsResponse>(
    '/chat/student_bulletin_threads',
    { params: { status } },
  );
  return response.data.items;
};

export interface StudentBulletinTutorResponse {
  tutor_id: number;
  other_user: ChatUser;
  source: 'response' | 'invitation';
  bulletin_response_id: number | null;
  student_proposal_id: number | null;
  response_status: string;
  chat_id: number | null;
  room_id: string | null;
  unread_count: number;
  last_message: LastMessage | null;
}

export interface StudentBulletinTutorsResponse {
  bulletin_id: number;
  items: StudentBulletinTutorResponse[];
  count: number;
}

export const fetchStudentBulletinTutors = async (
  bulletinId: number,
  status: ChatRoomStatus = 'active',
): Promise<StudentBulletinTutorResponse[]> => {
  const response = await api.get<StudentBulletinTutorsResponse>(
    '/chat/student_bulletin_tutors',
    { params: { bulletin_id: bulletinId, status } },
  );
  return response.data.items;
};

// Получить список чатов пользователя
export const fetchChatRooms = async (
  viewerRole?: ChatViewerRole,
  status?: ChatRoomStatus,
): Promise<ChatRoomResponse[]> => {
  try {
    const params: Record<string, string> = {};
    if (viewerRole) {
      params.viewer_role = viewerRole;
    }
    if (status) {
      params.status = status;
    }
    const response = await api.get<GetChatRoomsResponse>('/chat/rooms', {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data.items;
  } catch (error) {
    if (!axios.isAxiosError(error) || (error.response?.status ?? 0) >= 500) {
      console.error('[chat] Ошибка получения списка чатов:', error);
    }
    throw error;
  }
};

export interface SendChatMessageResponse {
  id: number;
  chat_id: number | null;
  room_id: string | null;
  user_id: number;
  msg: string;
  date_send: string;
  date_change: string;
}

// Получить историю сообщений (без тяжелых метаданных)
export const fetchChatHistory = async (chatId: number): Promise<ChatMessageResponse[]> => {
  try {
    const response = await api.get<ChatHistoryResponse>(`/chat/history/${chatId}`, {
      params: { include_meta: false },
      skipGlobalErrorHandler: true,
    });

    const messages = response.data.items.map((item) => ({
      id: item.id.toString(),
      text: item.msg,
      sender_id: item.user_id,
      receiver_id: 0,
      created_at: item.date_send,
      updated_at: item.date_change,
      status: item.status ?? ('sent' as const),
      sender_role: (item.sender_role as 'tutor' | 'student' | 'system' | undefined) ?? undefined,
      message_types: item.types ?? [],
    }));

    return messages.sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) {
      console.error(`[chat] Ошибка истории чата ${chatId}:`, error);
    }
    return [];
  }
};

export const fetchChatRoomById = async (
  chatId: number,
  viewerRole: ChatViewerRole,
): Promise<ChatRoomResponse | null> => {
  try {
    const response = await api.get<ChatRoomResponse>(`/chat/${chatId}/room`, {
      params: { viewer_role: viewerRole },
      skipGlobalErrorHandler: true,
    });
    return response.data;
  } catch (error) {
    if (!axios.isAxiosError(error) || (error.response?.status !== 403 && error.response?.status !== 404)) {
      console.error(`[chat] Ошибка room чата ${chatId}:`, error);
    }
    return null;
  }
};

export const fetchChatContactShare = async (
  chatId: number,
): Promise<ContactShareStateResponse | null> => {
  try {
    const response = await api.get<ContactShareStateResponse>(`/chat/${chatId}/contact_share`, {
      skipGlobalErrorHandler: true,
    });
    return response.data;
  } catch (error) {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) {
      console.error(`[chat] Ошибка contact_share чата ${chatId}:`, error);
    }
    return null;
  }
};

export const sendChatMessage = async (
  chatId: number,
  text: string,
  roomId?: string | null,
): Promise<SendChatMessageResponse> => {
  const response = await api.post<SendChatMessageResponse>(`/chat/${chatId}/message`, {
    msg: text,
    room_id: roomId ?? undefined,
  });
  return response.data;
};

export const markChatRead = async (chatId: number): Promise<void> => {
  try {
    await api.post(`/chat/${chatId}/read`, undefined, {
      skipGlobalErrorHandler: true,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return;
    }
    throw error;
  }
};

export const shareChatContacts = async (
  chatId: number,
): Promise<{ contact_share: ContactShareStateResponse; system_message_id: number | null }> => {
  const response = await api.post<{
    contact_share: ContactShareStateResponse;
    system_message_id: number | null;
  }>(`/chat/${chatId}/share_contacts`);
  return response.data;
};

export const openChatFromResponse = async (
  bulletinResponseId: number,
): Promise<{
  chat_id: number;
  room_id: string;
  bulletin_id: number;
  bulletin_response_id: number;
}> => {
  const response = await api.post<{
    chat_id: number;
    room_id: string;
    bulletin_id: number;
    bulletin_response_id: number;
  }>('/chat/open_from_response', {
    bulletin_response_id: bulletinResponseId,
  });
  return response.data;
};
